import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LegacyOrganizationAccount } from "../../../api/types/Account";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import {
  getDistanceByUnit,
  getLabelByUnit,
  getRoundedOffValue,
} from "../../../utils/distance";
import { useLoggedInUserData } from "../../../utils/user";
import { DISTANCE_UNIT_VALUES } from "../account/constants";
import { MaintenanceInputForm } from "./components/maintenanceInputForm";
import { MaintenancePerodicInputForm } from "./components/maintenancePerodicForm";
import { MaintenanceFlag } from "../../../api/types/Sysadmin";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { toast } from "react-toastify";
import { useLazyAssetMaintenanceFaultCodeQuery } from "../../../api/network/maintenanceApiService";
import svgIcons from "../../../assets/svg/svgIcons";
import { MaintenanceFaultCodeResponse } from "../../../api/types/Maintenance";

interface MaintenanceFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  distanceUnit?: string;
  maintenanceFlag?: MaintenanceFlag;
  deviceid?: string;
  setFaultCodeData?: React.Dispatch<
    React.SetStateAction<MaintenanceFaultCodeResponse | undefined>
  >;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const MaintenanceOdometerDetailForm: FC<MaintenanceFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  distanceUnit,
  deviceid,
  setFaultCodeData,
  setIsLoading,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance.detailsPage.form",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const [
    assetMaintenanceFaultCodeApiTrigger,
    {
      isFetching: isFetchingFaultCodesInfo,
      isLoading: isLoadingFaultCodesInfo,
      data: dataFaultCodesInfo,
    },
  ] = useLazyAssetMaintenanceFaultCodeQuery();

  useEffect(() => {
    setIsLoading?.(isLoadingFaultCodesInfo);
  }, [isLoadingFaultCodesInfo]);

  const handleViewDetailsButton = () => {
    assetMaintenanceFaultCodeApiTrigger({
      deviceid: deviceid,
    })
      .unwrap()
      .then((response) => {
        if (response) {
          setFaultCodeData?.(response);
        }
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.send_failed")];
        toast.error(errors?.join(" "));
      })
      .finally(() => {
        dispatch(
          setModalsData({
            ...modalsState,
            showModalMaintenanceFaultCodes: true,
          })
        );
      });
  };
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={`${t("reported_odometer")} (${t(getLabelByUnit(distanceUnit as DISTANCE_UNIT_VALUES))})`}
        type="text"
        id="reported_odometer"
        name="reported_odometer"
        placeholder="Please enter the reading"
        value={values?.reported_odometer || 0}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.reported_odometer}
        error={errors.reported_odometer}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("reported_engine_hours")}
        type="text"
        id="reported_engine_hours"
        name="reported_engine_hours"
        placeholder="Please enter the hours"
        value={getRoundedOffValue(values.reported_engine_hours)}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.reported_engine_hours}
        error={errors.reported_engine_hours}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <div className="col-span-12">
        <AdminFormFieldInput
          label={t("notes")}
          type="text"
          id="notes"
          name="notes"
          placeholder="Please leave a note if any"
          value={values.notes}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.notes}
          error={errors.notes}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <AdminFormFieldInput
        label={t("reminder_interval")}
        type="text"
        id="reminder_interval"
        name="reminder_interval"
        placeholder="Enter the reminder interval"
        value={values.reminder_interval}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.reminder_interval}
        error={errors.reminder_interval}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("reminder_message")}
        type="text"
        id="reminder_message"
        name="reminder_message"
        placeholder="Enter the reminder message"
        value={values.reminder_message}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.reminder_message}
        error={errors.reminder_message}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <div className="flex flex-1 items-end justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("fault_codes")}
          type="text"
          id="fault_codes"
          name="fault_codes"
          placeholder="No DTC's present"
          value={values.fault_codes}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.fault_codes}
          error={errors.fault_codes}
          disabled={!userCanEdit}
          detailsFormField={true}
          readOnly={true}
          customWrapperClass="flex-1"
        />
        {/* Loader */}
        <div className="col-span-6">
          {isFetchingFaultCodesInfo || isLoadingFaultCodesInfo ? (
            <div className="flex justify-center mt-0">
              <img
                src={svgIcons.LoadingAnimation}
                alt="Loading animation"
                className="w-12 h-12"
              />
            </div>
          ) : (
            <AdminFormFieldSubmit
              label={t("view_details")}
              buttonDimensionClass="h-11 w-full px-4"
              type="button"
              variant="primary"
              onClick={handleViewDetailsButton}
              disabled={!values.fault_codes}
            />
          )}
        </div>
      </div>
      {/* <AdminFormFieldInput
        label={t('configuration_string')}
        type="text"
        id="configuration_string"
        name="configuration_string"
        placeholder="Please provide a Configuration String"
        value={values.configuration_string}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.configuration_string}
        error={errors.configuration_string}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
    </div>
  );
};

export const MaintenanceIntervalDetailForm: FC<MaintenanceFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  distanceUnit,
  maintenanceFlag,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance.detailsPage.form",
  });
  const labels = JSON.parse(
    useLoggedInUserData("labels") || ""
  ) as LegacyOrganizationAccount;

  const formLabelsList = [
    {
      label: labels?.maintlabelkm0,

      maintenanceTypeId: "maintodometerkm0",
      maintenanceIntervalId: "maintintervalkm0",
    },
    {
      label: labels?.maintlabelkm1,

      maintenanceTypeId: "maintodometerkm1",
      maintenanceIntervalId: "maintintervalkm1",
    },
    {
      label: labels?.maintlabelkm2,

      maintenanceTypeId: "maintodometerkm2",
      maintenanceIntervalId: "maintintervalkm2",
    },
    {
      label: labels?.maintlabelkm3,

      maintenanceTypeId: "maintodometerkm3",
      maintenanceIntervalId: "maintintervalkm3",
    },
    {
      label: labels?.maintlabelkm4,

      maintenanceTypeId: "maintodometerkm4",
      maintenanceIntervalId: "maintintervalkm4",
    },
    {
      label: labels?.maintlabelkm5,

      maintenanceTypeId: "maintodometerkm5",
      maintenanceIntervalId: "maintintervalkm5",
    },
    {
      label: labels?.maintlabelkm6,

      maintenanceTypeId: "maintodometerkm6",
      maintenanceIntervalId: "maintintervalkm6",
    },
    {
      label: labels?.maintlabelkm7,

      maintenanceTypeId: "maintodometerkm7",
      maintenanceIntervalId: "maintintervalkm7",
    },
    {
      label: labels?.maintlabelkm8,

      maintenanceTypeId: "maintodometerkm8",
      maintenanceIntervalId: "maintintervalkm8",
    },
    {
      label: labels?.maintlabelkm9,

      maintenanceTypeId: "maintodometerkm9",
      maintenanceIntervalId: "maintintervalkm9",
    },
  ];

  console.log(maintenanceFlag, "maintenanceIntervalDetailForm");
  const filteredFormLabelsList = formLabelsList.filter((_, index) => {
    const flagKey = `maint_flag_km${index}` as keyof MaintenanceFlag;
    return maintenanceFlag?.[flagKey];
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {filteredFormLabelsList?.map((labelData) => (
        <MaintenanceInputForm
          key={labelData.maintenanceTypeId} // Add a unique key prop
          label={labelData.label} // Use the label from labelData
          maintenanceTypeId={labelData?.maintenanceTypeId}
          maintenanceIntervalId={labelData?.maintenanceIntervalId}
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          formikSetValue={formikSetValue}
          handleBlur={handleBlur}
          formikSetTouched={formikSetTouched}
          userCanEdit={userCanEdit}
          loadingData={loadingData}
          distanceUnit={distanceUnit?.toString()}
          bottomLabel={t(
            getLabelByUnit(distanceUnit?.toString() as DISTANCE_UNIT_VALUES)
          )}
        />
      ))}
    </div>
  );
};

export const MaintenanceLastMaintEngineHoursDetailForm: FC<
  MaintenanceFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  maintenanceFlag,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance.detailsPage.form",
  });
  const labels = JSON.parse(
    useLoggedInUserData("labels") || ""
  ) as LegacyOrganizationAccount;

  const formLabelsList = [
    {
      label: labels?.maintlabelhr0,

      maintenanceTypeId: "maintenghourshr0",
      maintenanceIntervalId: "maintintervalhr0",
    },
    {
      label: labels?.maintlabelhr1,

      maintenanceTypeId: "maintenghourshr1",
      maintenanceIntervalId: "maintintervalhr1",
    },
    {
      label: labels?.maintlabelhr2,

      maintenanceTypeId: "maintenghourshr2",
      maintenanceIntervalId: "maintintervalhr2",
    },
    {
      label: labels?.maintlabelhr3,

      maintenanceTypeId: "maintenghourshr3",
      maintenanceIntervalId: "maintintervalhr3",
    },
    {
      label: labels?.maintlabelhr4,

      maintenanceTypeId: "maintenghourshr4",
      maintenanceIntervalId: "maintintervalhr4",
    },
  ];
  console.log(maintenanceFlag, "MaintenanceLastMaintEngineHoursDetailForm");
  const filteredFormLabelsList = formLabelsList.filter((_, index) => {
    const flagKey = `maint_flag_hr${index}` as keyof MaintenanceFlag;
    return maintenanceFlag?.[flagKey];
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {filteredFormLabelsList?.map((labelData) => (
        <MaintenanceInputForm
          key={labelData.maintenanceTypeId} // Add a unique key prop
          label={labelData.label} // Use the label from labelData
          maintenanceTypeId={labelData?.maintenanceTypeId}
          maintenanceIntervalId={labelData?.maintenanceIntervalId}
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          formikSetValue={formikSetValue}
          handleBlur={handleBlur}
          formikSetTouched={formikSetTouched}
          userCanEdit={userCanEdit}
          loadingData={loadingData}
          bottomLabel="in hours"
          reportedValueId="reported_engine_hours"
        />
      ))}
    </div>
  );
};

export const MaintenanceLastPerodicServiceDateForm: FC<
  MaintenanceFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  maintenanceFlag,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance.detailsPage.form",
  });
  const labels = JSON.parse(
    useLoggedInUserData("labels") || ""
  ) as LegacyOrganizationAccount;

  const formLabelsList = [
    {
      label: labels?.maintlabelft0,

      maintenanceTypeId: "maintfixedtimeft0",
      serviceIntervalId: "maintintervalft0",
      notificationWindowId: "maintprenotifyft0",
    },
    {
      label: labels?.maintlabelft1,

      maintenanceTypeId: "maintfixedtimeft1",
      serviceIntervalId: "maintintervalft1",
      notificationWindowId: "maintprenotifyft1",
    },
    {
      label: labels?.maintlabelft2,

      maintenanceTypeId: "maintfixedtimeft2",
      serviceIntervalId: "maintintervalft2",
      notificationWindowId: "maintprenotifyft2",
    },
    {
      label: labels?.maintlabelft3,

      maintenanceTypeId: "maintfixedtimeft3",
      serviceIntervalId: "maintintervalft3",
      notificationWindowId: "maintprenotifyft3",
    },
    {
      label: labels?.maintlabelft4,

      maintenanceTypeId: "maintfixedtimeft4",
      serviceIntervalId: "maintintervalft4",
      notificationWindowId: "maintprenotifyft4",
    },
  ];

  console.log(maintenanceFlag, "MaintenanceLastPerodicServiceDateForm");
  const filteredFormLabelsList = formLabelsList.filter((_, index) => {
    const flagKey = `maint_flag_ft${index}` as keyof MaintenanceFlag;
    return maintenanceFlag?.[flagKey];
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {filteredFormLabelsList?.map((labelData) => (
        <MaintenancePerodicInputForm
          key={labelData.maintenanceTypeId} // Add a unique key prop
          label={labelData.label} // Use the label from labelData
          maintenanceTypeId={labelData?.maintenanceTypeId}
          serviceIntervalId={labelData?.serviceIntervalId}
          notificationWindowId={labelData?.notificationWindowId}
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          formikSetValue={formikSetValue}
          handleBlur={handleBlur}
          formikSetTouched={formikSetTouched}
          userCanEdit={userCanEdit}
          loadingData={loadingData}
        />
      ))}
    </div>
  );
};
